import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CloudLightning, CloudRain, Cloud, Sun, Heart, ClipboardText, CaretRight } from "@phosphor-icons/react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import PageHeader from "../components/PageHeader";
import { MOODS, supportForMood, moodByValue } from "../data/staticData";
import { getJSON, setJSON, K } from "../lib/storage";

const MOOD_ICONS = { CloudLightning, CloudRain, Cloud, Sun, Heart };

export default function Journal() {
  const [params] = useSearchParams();
  const [moods, setMoods] = useState(() => getJSON(K.moods, []));
  const [selected, setSelected] = useState(null);
  const [support, setSupport] = useState("");
  const [range, setRange] = useState(7);

  useEffect(() => {
    const preset = parseInt(params.get("mood"), 10);
    if (preset >= 1 && preset <= 5) logMood(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logMood = (value) => {
    setSelected(value);
    setSupport(supportForMood(value));
    const today = new Date().toISOString().slice(0, 10);
    const next = [...getJSON(K.moods, []).filter((m) => m.date !== today), { date: today, value }];
    next.sort((a, b) => (a.date < b.date ? -1 : 1));
    setJSON(K.moods, next);
    setMoods(next);
  };

  const cutoff = new Date(Date.now() - (range - 1) * 86400000).toISOString().slice(0, 10);
  const chartData = moods
    .filter((m) => m.date >= cutoff)
    .map((m) => ({
      date: m.date.slice(5).replace("-", "/"),
      value: m.value,
    }));

  return (
    <div>
      <PageHeader title="Journal d'humeur" subtitle="Un instant pour vous, chaque jour" />

      <section className="px-6">
        <div className="rounded-[26px] border border-[#36302B]/6 bg-white p-6 shadow-[0_4px_20px_rgba(54,48,43,0.03)]">
          <h2 className="font-display text-lg font-medium text-[#36302B]">
            Comment vous sentez-vous aujourd'hui ?
          </h2>
          <div className="mt-4 flex justify-between gap-2">
            {MOODS.map((m) => {
              const Icon = MOOD_ICONS[m.icon];
              const active = selected === m.value;
              return (
                <button
                  key={m.value}
                  data-testid={`journal-mood-${m.value}`}
                  onClick={() => logMood(m.value)}
                  className="flex flex-1 flex-col items-center gap-1.5 rounded-3xl border py-3 transition-all"
                  style={{
                    borderColor: active ? m.color : "rgba(54,48,43,0.08)",
                    backgroundColor: active ? `${m.color}18` : "transparent",
                  }}
                >
                  <Icon size={28} color={m.color} weight={active ? "fill" : "duotone"} />
                  <span className="text-[10.5px] font-semibold text-[#36302B]/60">{m.label}</span>
                </button>
              );
            })}
          </div>

          {support && (
            <div data-testid="journal-support" className="soft-fade mt-5 rounded-3xl bg-[#9CAEA9]/12 p-5">
              <p className="font-display text-[16px] italic leading-relaxed text-[#36302B]/85">{support}</p>
            </div>
          )}
        </div>
      </section>

      {/* History */}
      {moods.length > 0 && (
        <section className="mt-6 px-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-xl font-medium text-[#36302B]">Votre courbe douce</h2>
            <div className="flex gap-1 rounded-full bg-[#F2EDE4] p-1">
              {[7, 30].map((r) => (
                <button
                  key={r}
                  data-testid={`journal-range-${r}`}
                  onClick={() => setRange(r)}
                  className="rounded-full px-3 py-1.5 text-[12.5px] font-bold transition-colors"
                  style={{
                    backgroundColor: range === r ? "#C57B67" : "transparent",
                    color: range === r ? "#FFFFFF" : "rgba(54,48,43,0.55)",
                  }}
                >
                  {r} jours
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-[26px] border border-[#36302B]/6 bg-white p-4">
            <div style={{ width: "100%", height: 180 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="moodGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#C57B67" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#C57B67" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#36302B99" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[1, 5]} ticks={[1, 3, 5]} tick={{ fontSize: 11, fill: "#36302B99" }} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(v) => [moodByValue(v)?.label || v, "Humeur"]}
                    contentStyle={{ borderRadius: 14, border: "1px solid rgba(54,48,43,0.08)", fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#C57B67" strokeWidth={2.5} fill="url(#moodGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      {/* EPDS link */}
      <section className="mt-6 px-6">
        <Link
          to="/epds"
          data-testid="journal-epds-link"
          className="flex items-center gap-3 rounded-3xl border border-[#36302B]/6 bg-white p-5 shadow-sm transition-transform hover:-translate-y-1"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#D3B683]/20">
            <ClipboardText size={24} color="#B99248" weight="duotone" />
          </div>
          <div className="flex-1">
            <p className="font-display text-[16px] font-medium text-[#36302B]">Faire le point (test EPDS)</p>
            <p className="text-[13px] text-[#36302B]/55">Questionnaire d'Édimbourg · 10 questions, en douceur</p>
          </div>
          <CaretRight size={20} color="#36302B" weight="bold" className="opacity-30" />
        </Link>
      </section>

      <p className="mt-6 px-6 text-center text-[12px] leading-relaxed text-[#36302B]/45">
        Vos humeurs sont enregistrées uniquement sur cet appareil. Elles ne quittent jamais votre navigateur.
      </p>
    </div>
  );
}
